import type { LucideIcon } from 'lucide-react';
import type { ComponentPropsWithoutRef } from 'react';

import { Button } from './button.tsx';
import { icons } from './icons.ts';

// The operator's copy rules of 2026-10-17 (CB-CONV §7, v1.76): every Add button is a "+" and the noun,
// and a row's Edit and Remove are icons. Both keep a full name for screen readers and a tooltip
// (INV-13), so "+ Product" is still read as "Add Product" and the pencil as "Edit HP-1".

type ButtonProps = Omit<ComponentPropsWithoutRef<typeof Button>, 'children'>;

/** "+ Product": the noun shown, "Add Product" read aloud. A caller's own `aria-label` wins (it is
 * spread after), for a button whose noun is really a verb, like *+ Include*. */
export function AddButton({ noun, ...button }: ButtonProps & { noun: string }) {
  return (
    <Button aria-label={`Add ${noun}`} {...button}>
      <icons.add size={16} strokeWidth={2.5} aria-hidden />
      {noun}
    </Button>
  );
}

/** An icon-only control: `label` is its accessible name and its tooltip. */
export function IconButton({
  icon: Icon,
  label,
  className = '',
  ...button
}: ButtonProps & { icon: LucideIcon; label: string }) {
  return (
    <Button
      variant="quiet"
      aria-label={label}
      title={label}
      className={`px-2 ${className}`}
      {...button}
    >
      <Icon size={16} aria-hidden />
    </Button>
  );
}

export function EditButton({ what, ...button }: ButtonProps & { what: string }) {
  return <IconButton icon={icons.edit} label={`Edit ${what}`} {...button} />;
}

export function RemoveButton({ what, ...button }: ButtonProps & { what: string }) {
  return <IconButton icon={icons.remove} label={`Remove ${what}`} {...button} />;
}
